import type { Booking, BusinessDetail } from '../api/schemas';
import { isSameDay } from './format';

const DEFAULT_START_HOUR = 8;
const DEFAULT_END_HOUR = 20;
const HIDDEN = new Set<Booking['status']>(['released', 'cancelled']);
export const UNASSIGNED_COLUMN = 'sin-asignar';

export type CalendarBlock = {
  booking: Booking;
  /** Minutes from the top of the grid. */
  startMin: number;
  endMin: number;
  /** Side-by-side lanes when two visits share a column at once. */
  lane: number;
  lanes: number;
};

/** Any timed thing on the grid: a visit, or a class in a studio. */
export type TimedBlock<Item> = { item: Item; startMin: number; endMin: number; lane: number; lanes: number };

export type CalendarColumn = { id: string; name: string; blocks: CalendarBlock[] };

export type CalendarDay = {
  startHour: number;
  endHour: number;
  columns: CalendarColumn[];
  /** Visits on the grid (released and cancelled ones never are). */
  count: number;
};

/**
 * One day of the agenda laid out like a paper book: a column per person on the
 * roster, each visit a block from its start to its end. The hours stretch to
 * fit the earliest start and the latest end, never shrinking below 8 to 20.
 */
export function buildCalendarDay(
  bookings: Booking[],
  staff: BusinessDetail['staff'],
  day: Date,
  onlyStaffId?: string,
): CalendarDay {
  const visits = bookings.filter((booking) => !HIDDEN.has(booking.status) && isSameDay(new Date(booking.startsAt), day));
  const minutes = visits.map((booking) => ({ booking, start: minuteOfDay(booking.startsAt), end: minuteOfDay(booking.startsAt) + booking.durationMin }));
  const { startHour, endHour } = dayHours(minutes);
  const offset = startHour * 60;

  const roster = staff.filter((member) => !onlyStaffId || member.id === onlyStaffId);
  const rosterIds = new Set(roster.map((member) => member.id));
  const unassigned = minutes.filter((entry) => !entry.booking.staffId || !rosterIds.has(entry.booking.staffId));
  const columns: CalendarColumn[] = roster.map((member) => ({
    id: member.id,
    name: member.name,
    blocks: layOut(minutes.filter((entry) => entry.booking.staffId === member.id), offset),
  }));
  if (!onlyStaffId && unassigned.length > 0) {
    columns.push({ id: UNASSIGNED_COLUMN, name: 'Sin asignar', blocks: layOut(unassigned, offset) });
  }
  return { startHour, endHour, columns, count: columns.reduce((sum, column) => sum + column.blocks.length, 0) };
}

/** The grid's hours: stretched to fit the earliest start and the latest end, never shrinking below 8 to 20. */
export function dayHours(entries: { start: number; end: number }[]): { startHour: number; endHour: number } {
  return {
    startHour: Math.min(DEFAULT_START_HOUR, ...entries.map((entry) => Math.floor(entry.start / 60))),
    endHour: Math.max(DEFAULT_END_HOUR, ...entries.map((entry) => Math.ceil(entry.end / 60))),
  };
}

type Timed = { booking: Booking; start: number; end: number };

function layOut(entries: Timed[], offset: number): CalendarBlock[] {
  return layOutTimed(entries.map(({ booking, start, end }) => ({ item: booking, start, end })), offset).map(
    ({ item, ...block }) => ({ booking: item, ...block }),
  );
}

/** Greedy lanes: an entry takes the first lane free at its start; overlapping groups share the widest count. */
export function layOutTimed<Item>(entries: { item: Item; start: number; end: number }[], offset: number): TimedBlock<Item>[] {
  const sorted = [...entries].sort((a, b) => a.start - b.start || b.end - a.end);
  const laneEnds: number[] = [];
  const placed = sorted.map((entry) => {
    const free = laneEnds.findIndex((end) => end <= entry.start);
    const lane = free === -1 ? laneEnds.length : free;
    laneEnds[lane] = entry.end;
    return { entry, lane };
  });
  return placed.map(({ entry, lane }) => {
    const overlapping = placed.filter(({ entry: other }) => other.start < entry.end && entry.start < other.end);
    const lanes = Math.max(...overlapping.map((item) => item.lane)) + 1;
    return { item: entry.item, startMin: entry.start - offset, endMin: entry.end - offset, lane, lanes };
  });
}

export function minuteOfDay(iso: string): number {
  const date = new Date(iso);
  return date.getHours() * 60 + date.getMinutes();
}

/** "9:00" or "2:30", the compact clock a block has room for. */
export function shortClock(minuteOfDayValue: number): string {
  const hours = Math.floor(minuteOfDayValue / 60);
  const minutes = minuteOfDayValue % 60;
  const twelve = hours % 12 === 0 ? 12 : hours % 12;
  return `${twelve}:${String(minutes).padStart(2, '0')}`;
}
